import type { Move } from "../lib/cube";

interface Props {
  onMove: (m: Move) => void;
  onScramble: () => void;
  onSolve: () => void;
  onReset: () => void;
  canSolve: boolean;
  canScramble: boolean;
  busy: boolean;
  history: string[];
  scramble: string[];
}

const FACES: Move["face"][] = ["U", "D", "L", "R", "F", "B"];

export default function Controls({
  onMove,
  onScramble,
  onSolve,
  onReset,
  canSolve,
  canScramble,
  busy,
  history,
  scramble,
}: Props) {
  return (
    <aside className="sidebar">
      <header className="sidebar-header">
        <h1>Rubik's Cube</h1>
        <div className="meta">3×3 simulator with scramble and solve.</div>
      </header>

      <div className="actions">
        <button
          className="action primary"
          disabled={!canScramble}
          onClick={onScramble}
        >
          Scramble
        </button>
        <button
          className="action accent"
          disabled={!canSolve}
          onClick={onSolve}
          title={canSolve ? "Reverse the scramble" : "Generate a scramble first"}
        >
          Solve
        </button>
        <button className="action" disabled={busy} onClick={onReset}>
          Reset
        </button>
      </div>

      <div className="moves-section">
        <h2>Moves</h2>
        <div className="moves-grid">
          {FACES.map((f) => (
            <div key={f} className="moves-col">
              <button
                className="move-btn"
                onClick={() => onMove({ face: f })}
                disabled={busy}
              >
                {f}
              </button>
              <button
                className="move-btn"
                onClick={() => onMove({ face: f, prime: true })}
                disabled={busy}
              >
                {f}'
              </button>
              <button
                className="move-btn"
                onClick={() => onMove({ face: f, double: true })}
                disabled={busy}
              >
                {f}²
              </button>
            </div>
          ))}
        </div>
        <div className="hint">
          Keyboard: <kbd>U</kbd>/<kbd>D</kbd>/<kbd>F</kbd>/<kbd>B</kbd>/<kbd>L</kbd>/
          <kbd>R</kbd>. Hold <kbd>shift</kbd> for prime. Press <kbd>2</kbd> then a face
          for ½ turn.
        </div>
      </div>

      {scramble.length > 0 && (
        <div className="scramble-section">
          <h2>Scramble</h2>
          <div className="scramble-list">
            {scramble.map((m, i) => (
              <span key={i} className="move-token">
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="history-section">
        <h2>History ({history.length})</h2>
        {history.length === 0 ? (
          <p className="empty">No moves yet.</p>
        ) : (
          <div className="history-list">
            {history.map((m, i) => (
              <span key={i} className="move-token history">
                {m}
              </span>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
